import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import Button from './Button';

const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText = 'Confirmer', cancelText = 'Annuler', variant = 'danger' }) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const dialogContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998]"
          />

          {/* Dialog */}
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="relative w-full max-w-md"
            >
              <div className="relative bg-white rounded-3xl shadow-2xl border-2 border-gray-100 overflow-hidden">
                {/* Background Decoration */}
                <div className="absolute -right-10 -top-10 w-40 h-40 bg-gradient-to-br from-red-500 to-red-600 opacity-5 rounded-full blur-3xl" />
                
                {/* Close Button */}
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={onClose}
                  className="absolute top-4 right-4 p-2 rounded-xl hover:bg-gray-100 transition-colors z-10"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </motion.button>

                {/* Icon */}
                <div className="flex justify-center pt-8 pb-4">
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      rotate: [0, -5, 5, -5, 0]
                    }}
                    transition={{ 
                      duration: 0.5,
                      repeat: Infinity,
                      repeatDelay: 2
                    }}
                    className={`p-5 rounded-full ${
                      variant === 'danger' 
                        ? 'bg-red-500/20' 
                        : 'bg-amber-500/20'
                    }`}
                  >
                    <AlertTriangle className={`w-12 h-12 ${
                      variant === 'danger' 
                        ? 'text-red-600' 
                        : 'text-amber-600'
                    }`} />
                  </motion.div>
                </div>

                {/* Content */}
                <div className="px-8 pb-8 text-center relative">
                  <h3 className="text-2xl font-extrabold text-gray-900 mb-3">
                    {title}
                  </h3>
                  <p className="text-base text-gray-600 font-medium leading-relaxed mb-6">
                    {message}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="secondary"
                      onClick={onClose}
                      className="flex-1"
                    >
                      {cancelText}
                    </Button>
                    <Button
                      variant={variant}
                      onClick={handleConfirm}
                      className="flex-1"
                    >
                      {confirmText}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(dialogContent, document.body);
};

export default ConfirmDialog;


